import React, { useEffect, useRef, useState } from 'react';
import type { icModel } from 'generated-ts-common';
type QuestionnaireType = icModel.saf.infrastructure.model.virtualvisit.Questionnaire;
type Question = icModel.saf.infrastructure.model.virtualvisit.Question;
import { Button, Checkbox, intl, LoadMask } from 'ehs-react';
import { useQuestionnaireStore } from '../../stores/questionnaire.store';
import { useWaitingRoomStore } from '../../stores/waitingRoom.store';
import { RemoteContentCarousel } from '../RemoteContent/RemoteContentCarousel';
import { PortletStoreApi } from '../../App';

export function Questionnaire(): JSX.Element {
  const [sessionInitalized, waitingRoomTab] = useWaitingRoomStore(state => [
    state.sessionInitalized,
    state.waitingRoomTab
  ]);
  const [
    institutionQuestionnaire,
    loadQuestionnaire,
    sendQuestionnaire,
    loading,
    currentQuestionIdx,
    setCurrentQuestionIdx
  ] = useQuestionnaireStore(state => [
    state.institutionQuestionnaire,
    state.loadQuestionnaire,
    state.sendQuestionnaire,
    state.loading,
    state.currentQuestionIdx,
    state.setCurrentQuestionIdx
  ]);

  //focus the first choice of the next question when the current question is answered
  const prevQuestIdxRef = useRef<number | undefined>(currentQuestionIdx);
  const [focusFirstChoice, setFocusFirstChoice] = useState(false);
  useEffect(() => {
    if (prevQuestIdxRef.current !== currentQuestionIdx) {
      prevQuestIdxRef.current = currentQuestionIdx;
      setFocusFirstChoice(true);
    } else {
      setFocusFirstChoice(false);
    }
  }, [currentQuestionIdx]);

  useEffect(() => {
    if (sessionInitalized && institutionQuestionnaire === undefined) {
      loadQuestionnaire();
    }
  }, [sessionInitalized, loadQuestionnaire, institutionQuestionnaire]);

  const nextQuestion = () => {
    console.log('next question');
    if (institutionQuestionnaire) {
      const question =
        institutionQuestionnaire.questionnaires[currentQuestionIdx];
      sendQuestionnaire(question);
      setCurrentQuestionIdx(currentQuestionIdx + 1);
    }
  };

  const noOtherQuestions =
    currentQuestionIdx >=
    (institutionQuestionnaire?.questionnaires.length || 0);

  console.log(
    'enableRemoteContent',
    PortletStoreApi.getState().configuration.remoteContent.enableRemoteContent
  );

  if (
    noOtherQuestions &&
    PortletStoreApi.getState().configuration.remoteContent
      .enableRemoteContent &&
    waitingRoomTab === 'WAITING_ROOM' // only (re-)render remote content in waiting room to avoid weird formatting issue when switching back from chat
  ) {
    return <RemoteContentCarousel />;
  }

  const noQuestionnaireConfigured =
    institutionQuestionnaire?.questionnaires.length === 0;
  if (noQuestionnaireConfigured) {
    return <></>;
  }

  return (
    <form className="vvpp-questionnaire-container">
      {noOtherQuestions && !loading && <ThankYou />}
      {!noOtherQuestions && institutionQuestionnaire && (
        <>
          <div className="vvpp-questionnaire-content">
            <div className="vvpp-questionnaire-header">
              {intl('virtualvisitpatientportlet.questionnaire.header')}
            </div>
            <QuestionElement
              question={
                institutionQuestionnaire.questionnaires[currentQuestionIdx]
              }
              questionIdx={currentQuestionIdx}
              focusFirstChoice={focusFirstChoice}
            />
          </div>
          <div className="vvpp-questionnaire-navigation">
            <div
              id="vvpp-questionnaire-prev-question"
              className="send-message-icon ehealthicon-arrow-right vvpp-questionnaire-prev"
            />
            <div>
              {currentQuestionIdx + 1} /{' '}
              {institutionQuestionnaire.questionnaires.length}
            </div>
            <Button
              id="vvpp-questionnaire-next-question"
              variant="gray"
              text={
                intl('virtualvisitpatientportlet.questionnaire.header') +
                ' ' +
                intl('view.button.next')
              }
              icon="arrow-right"
              className="send-message-icon vvpp-questionnaire-next"
              onClick={nextQuestion}
            />
          </div>
        </>
      )}
      <LoadMask masked={loading} />
    </form>
  );
}

function ThankYou(): JSX.Element {
  return (
    <div className="vvpp-questionnaire-thankyou">
      {intl('virtualvisitpatientportlet.questionnaire.thank.you')}
    </div>
  );
}

function QuestionElement(props: {
  question: QuestionnaireType;
  questionIdx: number;
  focusFirstChoice: boolean;
}): JSX.Element {
  return (
    <fieldset className="vvpp-questionnaire-question">
      <legend>{props.question.title}</legend>
      {props.question.questions.map((choice, idx) => (
        <Choice
          key={props.questionIdx + '-' + choice.code}
          choice={choice}
          focused={props.focusFirstChoice && idx === 0}
        />
      ))}
    </fieldset>
  );
}

function Choice(props: { choice: Question; focused: boolean }): JSX.Element {
  const [checked, setChecked] = useState(props.choice.answer);
  const [focused, setFocused] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (props.focused && wrapperRef.current) {
      wrapperRef.current?.getElementsByTagName('input')[0]?.focus();
    }
  }, [wrapperRef, props.focused]);

  const onChange = (val: boolean) => {
    setChecked(val);
    props.choice.answer = val;
  };
  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);
  return (
    <div ref={wrapperRef}>
      <Checkbox
        className={
          'vvpp-questionnaire-checkbox' +
          (focused ? ' vvpp-choice-focused' : '')
        }
        label={props.choice.displayName}
        value={checked}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  );
}
